import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '../redux/hooks';
import { useLazyMyPointSalesQuery } from '../redux/api/user';
import { getOfflineReadiness, listOfflineSales } from './db';
import type { OfflineReadiness } from './types';
import {
  loadOfflineCatalogSnapshot,
  syncOfflineCatalogs,
  type OfflineCatalogSnapshot,
} from './catalogSync';
import { syncOfflineSales } from './saleSync';
import { loadOfflineTransactionSummary } from './offlineRouteLoader';
import { syncCroslandOfflineProducts } from './productBackgroundSync';
import {
  cacheUserPointOfSales,
  getOfflineUserKey,
  orderPointOfSalesForSync,
} from './pointOfSaleCache';
import { getOfflineSessionUser } from './sessionCache';
import type { IPointOfSale } from '../shared/models/core/point-of-sale';

interface OfflineContextValue {
  isOffline: boolean;
  offlineEnabled: boolean;
  readiness: OfflineReadiness;
  refreshReadiness: () => Promise<void>;
  syncNow: () => Promise<void>;
}

const emptyReadiness: OfflineReadiness = {
  enabled: false,
  ready: false,
  productCount: 0,
  correlativeCount: 0,
  pendingSales: 0,
};

const OfflineContext = createContext<OfflineContextValue>({
  isOffline: false,
  offlineEnabled: false,
  readiness: emptyReadiness,
  refreshReadiness: async () => undefined,
  syncNow: async () => undefined,
});

const DATA_REFRESH_INTERVAL_MS = 60 * 60 * 1000;
const BETWEEN_POS_DELAY_MS = 1200;

const waitForBackgroundSlot = async (delayMs = BETWEEN_POS_DELAY_MS): Promise<void> => {
  if ('requestIdleCallback' in window) {
    await new Promise<void>((resolve) => {
      window.requestIdleCallback(() => resolve(), { timeout: Math.max(2500, delayMs * 2) });
    });
  }
  await new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));
};

export const OfflineProvider = ({ children }: { children: React.ReactNode }) => {
  const selectedPos = useAppSelector((state) => state.auth.selectedPos);
  const offlineEnabled = !!selectedPos?.isOffline && selectedPos?.isDealer !== true;
  const [isOffline, setIsOffline] = useState(!navigator.onLine && offlineEnabled);
  const [readiness, setReadiness] = useState<OfflineReadiness>(emptyReadiness);
  const failures = useRef(0);
  const syncing = useRef(false);
  const productSyncing = useRef(false);
  const productSyncAbort = useRef<AbortController | null>(null);
  const [getPointSales] = useLazyMyPointSalesQuery();

  const refreshReadiness = useCallback(async () => {
    setReadiness(await getOfflineReadiness(selectedPos));
  }, [selectedPos]);

  const syncProductsInBackground = useCallback(
    async (pointOfSale: IPointOfSale, signal?: AbortSignal) => {
      if (
        !pointOfSale.isOffline ||
        pointOfSale.isDealer ||
        productSyncing.current ||
        !navigator.onLine
      ) {
        return;
      }

      const pending = await listOfflineSales(['pending', 'failed', 'conflict', 'syncing']);
      if (
        pending.some(
          (sale) =>
            sale.companyTaxId === pointOfSale.companyTaxId && sale.pointOfSaleId === pointOfSale.id
        )
      ) {
        return;
      }

      productSyncing.current = true;
      try {
        await syncCroslandOfflineProducts(pointOfSale, signal);
      } catch (error) {
        // Es una actualización de baja prioridad. Un fallo no debe interrumpir la venta online.
        console.warn(`No se pudo actualizar el stock local del PV ${pointOfSale.name}.`, error);
      } finally {
        productSyncing.current = false;
        if (selectedPos?.id === pointOfSale.id) await refreshReadiness();
      }
    },
    [refreshReadiness, selectedPos?.id]
  );

  const syncPointOfSale = useCallback(
    async (
      pointOfSale: IPointOfSale,
      catalogSnapshot: OfflineCatalogSnapshot,
      signal?: AbortSignal
    ) => {
      if (!pointOfSale.isOffline || pointOfSale.isDealer || !navigator.onLine || signal?.aborted)
        return;

      // Los catálogos pequeños sí pueden actualizarse aunque haya una venta pendiente. Los
      // correlativos se fusionan sin retroceder su valor local. Solo se posterga stock/productos
      // del PV, porque reemplazar esa proyección podría reponer una unidad ya reservada localmente.
      const remaining = await listOfflineSales(['pending', 'failed', 'conflict', 'syncing']);
      const hasPendingForPos = remaining.some(
        (sale) =>
          sale.companyTaxId === pointOfSale.companyTaxId && sale.pointOfSaleId === pointOfSale.id
      );

      await syncOfflineCatalogs(pointOfSale, catalogSnapshot);
      if (!pointOfSale.isDealer && !hasPendingForPos) {
        await syncProductsInBackground(pointOfSale, signal);
      }
    },
    [syncProductsInBackground]
  );

  const syncNow = useCallback(async () => {
    if (syncing.current || !navigator.onLine) return;

    const runSyncCycle = async () => {
      if (syncing.current || !navigator.onLine) return;
      syncing.current = true;

      const controller = new AbortController();
      productSyncAbort.current?.abort();
      productSyncAbort.current = controller;

      try {
        // La fuente autoritativa de PVs es la asociación del usuario. El PV persistido/seleccionado
        // se procesa primero porque es el que ePos usa como PV por defecto al iniciar la sesión.
        const associatedPointSales = await getPointSales(undefined, false).unwrap();
        const allAssociatedPointSales = Array.isArray(associatedPointSales)
          ? associatedPointSales
          : [];

        // La lista de asociaciones también queda disponible localmente. selectedPos es el PV
        // persistido por ePos y, por tanto, el PV preferido/por defecto para el siguiente inicio.
        const offlinePointSales = allAssociatedPointSales.filter(
          (pointOfSale) => pointOfSale.isOffline && !pointOfSale.isDealer
        );
        const cachedUser = getOfflineSessionUser();
        const userKey = getOfflineUserKey(cachedUser);
        if (userKey) {
          const defaultPointOfSaleId = offlinePointSales.some(
            (pointOfSale) => pointOfSale.id === selectedPos?.id
          )
            ? selectedPos?.id
            : offlinePointSales[0]?.id;
          // Una lista vacia elimina de forma efectiva las asociaciones dealer que una version
          // anterior hubiera dejado en el cache local del usuario.
          await cacheUserPointOfSales(userKey, offlinePointSales, defaultPointOfSaleId);
        }

        const orderedPointSales = orderPointOfSalesForSync(offlinePointSales, selectedPos?.id);

        if (!orderedPointSales.length) return;

        // Antes de descargar catálogos se drena el outbox de todos los PV. Las ventas tienen
        // prioridad absoluta sobre cualquier tráfico de precarga. El PV por defecto va primero.
        for (const pointOfSale of orderedPointSales) {
          if (controller.signal.aborted || !navigator.onLine) break;
          await syncOfflineSales({
            companyTaxId: pointOfSale.companyTaxId,
            pointOfSaleId: pointOfSale.id,
          });
        }

        if (controller.signal.aborted || !navigator.onLine) return;

        // Los catálogos compartidos se descargan una sola vez y se guardan para cada PV.
        // Esto evita repetir ubigeos, vendedores, correlativos, bancos, etc. por cada asociación.
        const catalogSnapshot = await loadOfflineCatalogSnapshot();

        for (let index = 0; index < orderedPointSales.length; index += 1) {
          if (controller.signal.aborted || !navigator.onLine) break;

          await syncPointOfSale(orderedPointSales[index], catalogSnapshot, controller.signal);

          // El PV por defecto/seleccionado queda listo primero. Los demás se continúan en espacios
          // de baja actividad y de forma secuencial para no competir con el uso normal de ePos.
          if (index < orderedPointSales.length - 1) {
            await waitForBackgroundSlot();
          }
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn(
            'No se pudo completar la sincronización en segundo plano de los PV asociados.',
            error
          );
        }
      } finally {
        if (productSyncAbort.current === controller) productSyncAbort.current = null;
        syncing.current = false;
        await refreshReadiness();
      }
    };

    // Evita que varias pestañas/PWA abiertas descarguen los mismos catálogos al mismo tiempo.
    const lockManager = (
      navigator as Navigator & {
        locks?: {
          request<T>(
            name: string,
            options: { ifAvailable: boolean },
            callback: (lock: object | null) => Promise<T>
          ): Promise<T>;
        };
      }
    ).locks;

    if (!lockManager) {
      await runSyncCycle();
      return;
    }

    await lockManager.request(
      'epos-offline-associated-point-of-sales',
      { ifAvailable: true },
      async (lock) => {
        if (!lock) return;
        await runSyncCycle();
      }
    );
  }, [getPointSales, refreshReadiness, selectedPos?.id, syncPointOfSale]);

  useEffect(() => {
    void refreshReadiness();
  }, [refreshReadiness]);

  useEffect(() => {
    if (!offlineEnabled) {
      failures.current = 0;
      setIsOffline(false);
      return;
    }

    let disposed = false;
    const check = async () => {
      if (!navigator.onLine) {
        failures.current = 6;
        if (!disposed) setIsOffline(true);
        return;
      }
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 1800);
      try {
        const response = await fetch('/api/healthz', {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        // Cualquier respuesta HTTP < 500 confirma que gateway/backend es alcanzable. 401/403 son
        // problemas de sesión/autorización y no deben cambiar la aplicación a modo sin Internet.
        if (response.status < 500) {
          const wasOffline = failures.current >= 5;
          failures.current = 0;
          if (!disposed) setIsOffline(false);
          if (wasOffline) void syncNow();
        } else {
          failures.current += 1;
        }
      } catch {
        failures.current += 1;
      } finally {
        window.clearTimeout(timeout);
      }
      if (!disposed && failures.current >= 5) setIsOffline(true);
    };

    void check();
    const timer = window.setInterval(check, 2500);
    const onlineHandler = () => void check();
    const offlineHandler = () => {
      failures.current = 6;
      productSyncAbort.current?.abort();
      setIsOffline(true);
    };
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, [offlineEnabled, syncNow]);

  // La sincronización no depende únicamente del PV que está abierto. Si el usuario tiene varios PV
  // asociados con Company.IsOffline, se preparan todos en background y el PV por defecto va primero.
  useEffect(() => {
    if (selectedPos && navigator.onLine) void syncNow();
  }, [selectedPos?.id, syncNow]);

  useEffect(() => {
    if (!selectedPos) return;

    const timer = window.setInterval(() => {
      if (navigator.onLine) void syncNow();
    }, DATA_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [selectedPos?.id, syncNow]);

  useEffect(() => {
    return () => productSyncAbort.current?.abort();
  }, []);

  useEffect(() => {
    if (!offlineEnabled || !navigator.onLine) return;

    void loadOfflineTransactionSummary().catch((error) => {
      console.warn('No se pudo precargar el resumen para la venta offline.', error);
    });
  }, [offlineEnabled, isOffline]);

  const value = useMemo(
    () => ({
      isOffline: isOffline && offlineEnabled,
      offlineEnabled,
      readiness,
      refreshReadiness,
      syncNow,
    }),
    [isOffline, offlineEnabled, readiness, refreshReadiness, syncNow]
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOffline = () => useContext(OfflineContext);
